import { useQuery } from '@tanstack/react-query';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { useSeekAuth } from '@/contexts/SeekAuthContext';
import {
  hasComputedSeekMatchScore,
  resolveSeekMatchScoreState,
  seekMatchScoreSortValue,
  type SeekMatchScoreDisplay,
} from '@/lib/seekMatchScoreState';

const MISSING_MATCH_SCORE: SeekMatchScoreDisplay = { state: 'missing', score: null };

export function useRecommendedJobs(limit = 5) {
  const { user } = useSeekAuth();
  const uid = user?.id;

  const query = useQuery({
    queryKey: ['recommended-jobs', uid, limit],
    queryFn: async () => {
      // Fetch top match scores for this candidate
      const [{ data: scores, error: scoresErr }, { data: profile }] = await Promise.all([
        seekSupabase
          .from('job_match_scores')
          .select('seek_job_catalog_id, match_score, updated_at')
          .eq('candidate_id', uid!)
          .order('match_score', { ascending: false })
          .limit(limit + 10), // fetch extra to filter out applied jobs
        seekSupabase
          .from('candidate_profiles')
          .select('updated_at')
          .eq('candidate_id', uid!)
          .maybeSingle(),
      ]);

      if (scoresErr) throw scoresErr;
      if (!scores || scores.length === 0) return [];
      const profileUpdatedAt = profile?.updated_at ?? null;

      // Get candidate's applied job catalog IDs to exclude
      const { data: apps } = await seekSupabase
        .from('seek_applications')
        .select('seek_job_catalog_id')
        .eq('candidate_id', uid!);

      const appliedIds = new Set((apps ?? []).map((application) => application.seek_job_catalog_id));

      // Filter out already-applied jobs
      const filtered = scores.filter(
        (score) => !appliedIds.has(score.seek_job_catalog_id)
      );
      const topIds = filtered.slice(0, limit).map((score) => score.seek_job_catalog_id);
      if (topIds.length === 0) return [];

      // Fetch job details for those IDs
      const { data: jobs, error: jobsErr } = await seekSupabase
        .from('seek_job_catalog')
        .select('*')
        .in('id', topIds)
        .eq('status', 'active')
        .eq('visibility_mode', 'seek_public')
        .eq('is_accepting_applications', true);

      if (jobsErr) throw jobsErr;

      // Build score map and merge
      const scoreMap: Record<string, SeekMatchScoreDisplay> = Object.fromEntries(
        scores.map((score) => {
          const display = resolveSeekMatchScoreState({
            score: score.match_score,
            computedAt: score.updated_at,
            profileUpdatedAt,
          });
          return [score.seek_job_catalog_id, display];
        })
      );

      return (jobs ?? [])
        .map((job) => {
          const display = scoreMap[job.id] ?? MISSING_MATCH_SCORE;
          return {
            ...job,
            match_score: hasComputedSeekMatchScore(display) ? display.score : null,
            match_score_state: display.state,
          };
        })
        .sort((a, b) => seekMatchScoreSortValue(scoreMap[b.id] ?? MISSING_MATCH_SCORE) - seekMatchScoreSortValue(scoreMap[a.id] ?? MISSING_MATCH_SCORE))
        .slice(0, limit);
    },
    enabled: !!uid,
  });

  return {
    recommendations: query.data ?? [],
    isLoading: query.isLoading,
  };
}
